import { isWebGLAvailable } from './game/webgl.js';
import type { Difficulty, Level } from '@color-dots/game';

export type GameMode = '3d' | '2d';

export type DifficultyTier = 'beginner' | 'easy' | 'medium' | 'hard' | 'expert';

export interface GameSettings {
  use3d: boolean;
  sound: boolean;
  music: boolean;
  animations: boolean;
  difficultyTier: DifficultyTier;
}

export interface DifficultyTierInfo {
  id: DifficultyTier;
  label: string;
  description: string;
  maxLevel: number;
}

export const DIFFICULTY_TIERS: DifficultyTierInfo[] = [
  {
    id: 'beginner',
    label: 'Новичок',
    description: 'Уровни 1–15, знакомство с механикой',
    maxLevel: 15,
  },
  {
    id: 'easy',
    label: 'Лёгкий',
    description: 'Уровни 1–30, небольшие сетки',
    maxLevel: 30,
  },
  {
    id: 'medium',
    label: 'Средний',
    description: 'Уровни 1–60, больше пар и размеров',
    maxLevel: 60,
  },
  {
    id: 'hard',
    label: 'Сложный',
    description: 'Уровни 1–90, плотные раскладки',
    maxLevel: 90,
  },
  {
    id: 'expert',
    label: 'Эксперт',
    description: 'Все 250 уровней без ограничений',
    maxLevel: 250,
  },
];

const SETTINGS_KEY = 'color-dots-settings:v1';
const USE_3D_KEY = 'color-dots-use-3d';

const DEFAULT_SETTINGS: GameSettings = {
  use3d: false,
  sound: false,
  music: false,
  animations: true,
  difficultyTier: 'easy',
};

function isDifficultyTier(value: unknown): value is DifficultyTier {
  return (
    value === 'beginner' ||
    value === 'easy' ||
    value === 'medium' ||
    value === 'hard' ||
    value === 'expert'
  );
}

export function getLevelNumber(levelId: string): number {
  const match = levelId.match(/\d+/);
  return match ? parseInt(match[0], 10) : 1;
}

export function getDifficultyTierInfo(tier: DifficultyTier): DifficultyTierInfo {
  return DIFFICULTY_TIERS.find((entry) => entry.id === tier) ?? DIFFICULTY_TIERS[1]!;
}

export function getMaxLevelForTier(tier: DifficultyTier): number {
  return getDifficultyTierInfo(tier).maxLevel;
}

export function isLevelInTier(levelId: string, tier: DifficultyTier): boolean {
  return getLevelNumber(levelId) <= getMaxLevelForTier(tier);
}

export function filterLevelsByTier(levels: Level[], tier: DifficultyTier): Level[] {
  const maxLevel = getMaxLevelForTier(tier);
  return levels.filter((level) => getLevelNumber(level.id) <= maxLevel);
}

export function clampLevelToTier(
  levelId: string,
  tier: DifficultyTier,
  levels: Level[],
): string {
  const allowed = filterLevelsByTier(levels, tier);
  if (allowed.length === 0) return levelId;
  if (isLevelInTier(levelId, tier)) return levelId;
  return allowed[allowed.length - 1]!.id;
}

export function getStoredSettings(): GameSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<GameSettings>;
      return {
        use3d: parsed.use3d !== false,
        sound: parsed.sound !== false,
        music: parsed.music !== false,
        animations: parsed.animations !== false,
        difficultyTier: isDifficultyTier(parsed.difficultyTier)
          ? parsed.difficultyTier
          : DEFAULT_SETTINGS.difficultyTier,
      };
    }
  } catch {
    // ignore malformed storage
  }

  const legacyUse3d = localStorage.getItem(USE_3D_KEY);
  if (legacyUse3d !== null) {
    return {
      ...DEFAULT_SETTINGS,
      use3d: legacyUse3d !== 'false',
    };
  }

  return { ...DEFAULT_SETTINGS };
}

export function saveSettings(settings: GameSettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  localStorage.setItem(USE_3D_KEY, String(settings.use3d));
}

export function getStoredUse3D(): boolean {
  return getStoredSettings().use3d;
}

export function setStoredUse3D(enabled: boolean): void {
  saveSettings({ ...getStoredSettings(), use3d: enabled });
}

export function resolveGameMode(settings: GameSettings = getStoredSettings()): GameMode {
  if (!settings.use3d) return '2d';
  return isWebGLAvailable() ? '3d' : '2d';
}

export function canUse3D(): boolean {
  return isWebGLAvailable();
}

export function applyAnimationSetting(enabled: boolean): void {
  document.documentElement.dataset.animations = enabled ? 'on' : 'off';
}

export function difficultyTierLabel(tier: DifficultyTier): string {
  return getDifficultyTierInfo(tier).label;
}

const LEVEL_DIFFICULTY_LABELS: Record<Difficulty, string> = {
  easy: 'Лёгкий',
  medium: 'Средний',
  hard: 'Сложный',
  expert: 'Эксперт',
};

/** A level's own difficulty (from its data), not the player's browsing tier filter —
 *  distinct from {@link difficultyTierLabel}. Levels 1-120 are all "easy"/"medium"/"hard"
 *  regardless of which unlock tier the player has selected in settings. */
export function levelDifficultyLabel(difficulty: Difficulty): string {
  return LEVEL_DIFFICULTY_LABELS[difficulty];
}
